/*
 * @Author: Ryan
 * @Date: 2026-01-16
 * @Description: E-Commerce Landing 页面配置
 */
import { homeURL } from '@/utils/routeConstants';

import { LogoItem } from '../components/common/LogoMarquee';
import { VideoItem } from '../components/common/VideoCarousel';
import { SEOHeadProps } from '../components/SEOHead';
import {
  ComparisonColumn,
  ComparisonRow,
} from '../components/templates/ComparisonTableSection';
import type { FAQItem } from '../components/templates/FAQSection';
export { parseFaqInlineMarkdownLinks } from '../components/templates/FAQSection';
import { FeatureCardItem } from '../components/templates/FeatureCardsSection';
import { FeatureItem } from '../components/templates/FeaturesSection';
import { ImageCardItem } from '../components/templates/ImageCardsSection';
import { ROIStatCard } from '../components/templates/ROIStatsSection';
import { ShowcaseItem } from '../components/templates/ShowcaseCarouselSection';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createOrganizationSchema,
  createSoftwareAppSchema,
} from '../utils/structuredData';

/** 页面基础 URL */
const PAGE_URL = 'https://falcocut.ai';

/** FAQ Schema 数据（简化版，用于结构化数据） */
const FAQ_SCHEMA_DATA = [
  {
    question: 'What is an AI Video Agent?',
    answer:
      'An AI Video Agent is a smart, creative partner that helps you turn ideas into polished videos with far less effort. It understands your goals, guides key steps, and handles time-consuming tasks like scene planning, pacing, and visual decisions.',
  },
  {
    question: 'What is FalcoCut and how does it work?',
    answer:
      'FalcoCut is an AI Video Agent that helps you turn simple ideas into complete videos without needing editing skills. You describe what you want, and the system builds scenes, timing, and visuals automatically.',
  },
  {
    question: 'What types of videos can I create with FalcoCut?',
    answer:
      'FalcoCut lets you create marketing videos, product showcases, UGC-style clips, avatar-driven content, e-commerce formats, training content, and educational explainers.',
  },
  {
    question: 'How is FalcoCut different from traditional video production?',
    answer:
      'Traditional video tools require manual editing. FalcoCut simplifies the process by understanding your instructions and automatically shaping scenes, pacing, and visuals around your goals.',
  },
  {
    question: 'Do I need video editing experience to use FalcoCut?',
    answer:
      'No editing background is needed. FalcoCut guides you through each step clearly, making it simple for anyone to create professional-looking content.',
  },
  {
    question: 'How long does it take to generate an AI video?',
    answer:
      'Most videos are created within minutes, giving you results quickly enough to review and adjust right away.',
  },
  {
    question: 'Is FalcoCut free?',
    answer:
      'FalcoCut offers a free plan with 10 monthly credits. Exported videos include a watermark until you upgrade to a paid plan.',
  },
];

/** SEO 配置 */
export const SEO_CONFIG: SEOHeadProps = {
  title: 'FalcoCut: AI-Powered Video Agent for Marketing, UGC & Product Content',
  description:
    'Generate natural UGC, compelling ad creatives, and clear product videos using FalcoCut AI video agent. Turn simple ideas into ready-to-publish content for social media, online stores, and paid campaigns. Start creating with FalcoCut and experience effortless video production.',
  url: PAGE_URL,
  image: 'https://falcocut.ai/images/og-img.png',
  publishedTime: '2026-01-16',
  modifiedTime: '2026-01-23',
  // 首屏关键图片预加载
  preloadImages: ['https://assets.falcocut.ai/video-agent/partner-merchants.webp'],
  structuredData: [
    // SoftwareApplication Schema
    createSoftwareAppSchema({
      name: 'FalcoCut - AI Video Agent',
      description:
        'Generate natural UGC, compelling ad creatives, and clear product videos using FalcoCut AI video agent. Turn simple ideas into ready-to-publish content for social media, online stores, and paid campaigns.',
      url: PAGE_URL,
      applicationCategory: 'MultimediaApplication',
      ratingValue: '4.8',
      ratingCount: '937',
      featureList: [
        'AI Video Generation',
        'UGC Content Creation',
        'Product Videos',
        'AI Avatars',
        'Voice Cloning',
        'Video Translation',
      ],
    }),
    // Organization Schema
    createOrganizationSchema({
      name: 'FalcoCut',
      url: PAGE_URL,
      logo: 'https://falcocut.ai/images/logo.png',
    }),
    // FAQPage Schema
    createFAQSchema({
      faqs: FAQ_SCHEMA_DATA,
    }),
    // BreadcrumbList Schema
    createBreadcrumbSchema({
      items: [{ name: 'Home', url: PAGE_URL }],
    }),
  ],
};

/** 首屏文案配置 */
export const HERO_CONFIG = {
  title: 'AI E-Commerce Video Generator',
  highlightTitle: '10x Faster & Smarter',
  description:
    'Make studio-quality videos with AI voice cloning, avatars, face swap, and translation. Choose from 180+ voices, 50+ avatars, and 7+ languages to cut production time and costs instantly.',
  ctaText: 'Create Free Video Now',
  ctaUrl: homeURL,
  logoImage: 'https://assets.falcocut.ai/video-agent/partner-merchants.webp',
};

/** 视频列表配置 */
export const HERO_VIDEOS: VideoItem[] = Array.from({ length: 18 }, (_, i) => ({
  id: `video-${i + 1}`,
  videoUrl: '',
  posterUrl: '',
  alt: `AI Generated Video ${i + 1}`,
}));

/** Features Section 配置 */
export const FEATURES_CONFIG = {
  title: 'Build High‑Converting Videos Instantly with AI',
  subtitle:
    'Transform concepts into ready‑to‑publish videos in moments through a fast, flexible creation flow. Winning creatives rarely rely on a single feature, so mix different generation modes to produce standout marketing and e-commerce content without slow, traditional steps.',
  ctaText: 'Start Creating',
};

/** Features 列表 */
export const FEATURES_LIST: FeatureItem[] = [
  {
    title: 'Text to Video',
    description:
      'Turn a prompt, script, or product description into a refined video that flows with natural pacing and clear visual storytelling, then carry that momentum into ad hooks, explainers, and social‑ready marketing clips that feel professionally crafted.',
    imageUrl: 'https://assets.falcocut.ai/video-agent/text-to-video.webp',
    imageAlt: 'FalcoCut Text to Video - Generate Your Video',
    ctaText: 'Generate Your Video',
    ctaUrl: '/ai-video-generator',
  },
  {
    title: 'Image to Video',
    description:
      'Upload a product photo and watch it evolve into a fluid, dynamic sequence with motion, transitions, and human presence, allowing your visuals to shift from static assets to immersive e-commerce showcases and lifestyle moments that elevate your brand story.',
    imageUrl: 'https://assets.falcocut.ai/video-agent/image-to-video.webp',
    imageAlt: 'FalcoCut Image to Video - Create Your Video',
    ctaText: 'Create Your Video',
    ctaUrl: '/ai-video-generator',
  },
  {
    title: 'Text to Image',
    description:
      'Describe the mood, style, or composition you envision and generate polished visuals that align seamlessly with your brand, giving your campaigns expressive product shots, backgrounds, thumbnails, and creative elements in an instant.',
    imageUrl: 'https://assets.falcocut.ai/video-agent/text-to-image.webp',
    imageAlt: 'FalcoCut Text to Image - Create Your Image',
    ctaText: 'Create Your Image',
    ctaUrl: '/ai-image-generator',
  },
];

/** 合作品牌 Logo 列表 */
export const PARTNER_LOGOS: LogoItem[] = [
  {
    id: 'grammarly',
    src: '/images/landing/logos/grammarly.svg',
    alt: 'Grammarly',
    width: 140,
    height: 32,
  },
  {
    id: 'salesforce',
    src: '/images/landing/logos/salesforce.svg',
    alt: 'Salesforce',
    width: 100,
    height: 32,
  },
  {
    id: 'omron',
    src: '/images/landing/logos/omron.svg',
    alt: 'OMRON',
    width: 120,
    height: 32,
  },
  {
    id: 'pwc',
    src: '/images/landing/logos/pwc.svg',
    alt: 'PwC',
    width: 80,
    height: 32,
  },
  {
    id: 'uber',
    src: '/images/landing/logos/uber.svg',
    alt: 'Uber',
    width: 80,
    height: 32,
  },
  {
    id: 'tulane',
    src: '/images/landing/logos/tulane.svg',
    alt: 'Tulane University',
    width: 100,
    height: 40,
  },
  {
    id: 'hitachi',
    src: '/images/landing/logos/hitachi.svg',
    alt: 'Hitachi Solutions',
    width: 150,
    height: 32,
  },
  {
    id: 'eventhub',
    src: '/images/landing/logos/eventhub.svg',
    alt: 'EventHub',
    width: 120,
    height: 32,
  },
  {
    id: 'cocacola',
    src: '/images/landing/logos/cocacola.svg',
    alt: 'Coca-Cola',
    width: 100,
    height: 32,
  },
];

/** 客户品牌 Logo 列表 */
export const CUSTOMER_LOGOS: LogoItem[] = [
  {
    id: 'anker',
    src: '/images/brand-logo/anker.png',
    alt: 'Anker',
    width: 140,
    height: 60,
  },
  {
    id: 'dji',
    src: '/images/brand-logo/dji.png',
    alt: 'DJI',
    width: 92,
    height: 60,
  },
  {
    id: 'huaxizi',
    src: '/images/brand-logo/huaxizi.png',
    alt: 'Florasis',
    width: 134,
    height: 60,
  },
  {
    id: 'haier',
    src: '/images/brand-logo/haier.png',
    alt: 'Haier',
    width: 122,
    height: 60,
  },
  {
    id: 'honor',
    src: '/images/brand-logo/honor.png',
    alt: 'Honor',
    width: 140,
    height: 60,
  },
  {
    id: '360',
    src: '/images/brand-logo/360.png',
    alt: '360',
    width: 120,
    height: 60,
  },
  {
    id: 'tiktok',
    src: '/images/brand-logo/tiktok.png',
    alt: 'TikTok',
    width: 150,
    height: 60,
  },
  {
    id: 'chuanyin',
    src: '/images/brand-logo/chuanyin.png',
    alt: 'Transsion',
    width: 180,
    height: 60,
  },
  {
    id: 'vivavideo',
    src: '/images/brand-logo/vivavideo.png',
    alt: 'VivaVideo',
    width: 154,
    height: 60,
  },
  {
    id: 'xiaomi',
    src: '/images/brand-logo/xiaomi.png',
    alt: 'Xiaomi',
    width: 80,
    height: 60,
  },
];

/** Showcase Carousel 配置 */
export const SHOWCASE_CONFIG = {
  title: 'Reimagine Trending Video Looks with AI Video Editor',
  subtitle:
    "Swap people, products, or backgrounds with effortless, text-based edits. Whether you're refining a viral hook or updating a product scene, FalcoCut makes video editing feel fast, clear, and easy to follow.",
  ctaText: 'Edit Your Video Now',
  ctaUrl: '/ai-video-generator',
  autoPlayInterval: 5000,
};

/** Showcase 轮播项列表 */
export const SHOWCASE_ITEMS: ShowcaseItem[] = [
  {
    id: 'showcase-1',
    imageUrl: 'https://assets.falcocut.ai/video-agent/change-characters-with-ai.webp',
    imageAlt: 'AI Video Editor - Change characters with AI-powered editing',
  },
  {
    id: 'showcase-2',
    imageUrl: 'https://assets.falcocut.ai/video-agent/replace-products.webp',
    imageAlt: 'AI Video Editor - Replace products and backgrounds seamlessly',
  },
  {
    id: 'showcase-3',
    imageUrl: 'https://assets.falcocut.ai/video-agent/showcase-products.webp',
    imageAlt: 'AI Video Editor - Create professional product videos with AI',
  },
];

/** Feature Cards Section 配置 */
export const FEATURE_CARDS_CONFIG = {
  title: 'AI Avatar',
  highlightTitle: 'That Speak, Showcase, and Sell',
  subtitle:
    'Make studio-quality avatar videos that deliver your message, highlight your products, and keep your brand consistent across every campaign. Ideal for product demos, ad creatives, and how-to explainers that benefit from a dependable, always-ready digital presenter.',
  ctaText: 'Create Your Own Avatar',
  ctaUrl: '/ai-avatar-generator',
};

/** Feature Cards 列表 */
export const FEATURE_CARDS: FeatureCardItem[] = [
  {
    id: 'showcase-products-avatar',
    title: 'Showcase Products with Avatar',
    description:
      'Transform any photo into an expressive brand representative for high-performing ads and sales funnels.',
    href: '/ai-avatar-generator',
    videos: [
      {
        id: 'avatar-1',
        videoUrl: '',
        posterUrl: 'https://assets.falcocut.ai/video-agent/product-explainer-avatar.webp',
        alt: 'Product Explainer Avatar',
      },
      {
        id: 'avatar-2',
        videoUrl: '',
        posterUrl: 'https://assets.falcocut.ai/video-agent/beauty-product-avatar.webp',
        alt: 'Beauty Product Avatar',
      },
      {
        id: 'avatar-3',
        videoUrl: '',
        posterUrl:
          'https://assets.falcocut.ai/video-agent/lifestyle-avatar-presentation.webp',
        alt: 'Lifestyle Avatar Presentation',
      },
    ],
  },
  {
    id: 'ugc-style-avatar',
    title: 'Generate Your Product Avatar Video Now',
    description:
      'Turn static items into authentic, UGC-style videos featuring avatars that showcase your products in action.',
    href: '/ai-avatar-generator',
    videos: [
      {
        id: 'avatar-4',
        videoUrl: '',
        posterUrl: 'https://assets.falcocut.ai/video-agent/desk-product-demo-avatar.webp',
        alt: 'Desk Product Demo Avatar',
      },
      {
        id: 'avatar-5',
        videoUrl: '',
        posterUrl:
          'https://assets.falcocut.ai/video-agent/full-body-product-demo-avatar.webp',
        alt: 'Full Body Product Demo Avatar',
      },
      {
        id: 'avatar-6',
        videoUrl: '',
        posterUrl:
          'https://assets.falcocut.ai/video-agent/lifestyle-avatar-presentation.webp',
        alt: 'Lifestyle Avatar Presentation',
      },
    ],
  },
];

/** AI Voice Section 配置（深色主题） */
export const AI_AVATAR_CONFIG = {
  title: 'Emotion-Driven AI Voice for Natural',
  highlightTitle: 'Text-to-Speech',
  subtitle:
    "Build trust and scale your brand's reach with emotionally intelligent voices tailored for audiences across regions. Transform your marketing scripts into professional performances that drive higher engagement and conversion rates.",
  ctaText: 'Clone Your Voice',
  ctaUrl: '/ai-voice-cloning',
};

/** AI Voice Cards 列表 */
export const AI_AVATAR_CARDS: ImageCardItem[] = [
  {
    id: 'speech-to-speech',
    title: 'Speech to Speech',
    description:
      'Scale your personalized outreach by instantly cloning your voice to maintain a consistent brand persona in every message.',
    imageUrl:
      'https://assets.falcocut.ai/video-agent/voice-cloning-across-languages.webp',
    imageAlt: 'Voice Cloning Across Languages',
  },
  {
    id: 'text-to-speech',
    title: 'Text to Speech',
    description:
      'Reach worldwide markets using 100+ premium AI voices to turn any script into a strong-performing narration in any language.',
    imageUrl: 'https://assets.falcocut.ai/video-agent/emotion-aware-text-to-speech.webp',
    imageAlt: 'Emotion-Aware Text to Speech',
  },
];

/** Video Translator Section 配置（浅色主题） */
export const VIDEO_TRANSLATOR_CONFIG = {
  title: 'Go Global with AI Video Translation',
  subtitle:
    'Localize your existing social media content into 30+ languages to reach international customers in record time—no re-shooting or translators required. Break through linguistic barriers to ensure your product message is understood and felt by audiences everywhere.',
  ctaText: 'Translate Your Videos',
  ctaUrl: '/video-translator',
};

/** Video Translator Cards 列表 */
export const VIDEO_TRANSLATOR_CARDS: ImageCardItem[] = [
  {
    id: 'auto-subtitle-lip-sync',
    title: 'Auto-Subtitle & Lip Sync',
    description:
      'Automatically generate and time-align accurate subtitles to make your creatives accessible and inclusive for viewers across every territory. Synchronize lip movements perfectly with the translated audio for a convincing, high-fidelity experience that keeps viewers focused on your product.',
    imageUrl: 'https://assets.falcocut.ai/video-agent/auto-subtitles-and-lip-sync.webp',
    imageAlt: 'Auto-Subtitle and Lip Sync for Video Translation',
  },
  {
    id: 'auto-translate-videos',
    title: 'Auto-Translate Videos',
    description:
      'Effortlessly convert your footage into localized versions while preserving the original energy and brand personality of your message. Replace original audio with premium voice synthesis for a clear, persuasive sound that resonates better with local customers.',
    imageUrl: 'https://assets.falcocut.ai/video-agent/auto-translate-videos.webp',
    imageAlt: 'Auto-Translate Videos with AI',
  },
];

/** Video Showcase Section 配置 */
export const VIDEO_SHOWCASE_CONFIG = {
  title: 'The complete toolkit for modern advertisers',
  subtitle:
    'Make studio-quality videos with AI voice cloning, avatars, face swap, and translation. Choose from 180+ voices, 50+ avatars, and 7+ languages to cut production time and costs instantly.',
  ctaText: 'Create Free Video Now',
  ctaUrl: '/ai-video-generator',
};

/** Video Showcase 视频列表 */
export const VIDEO_SHOWCASE_VIDEOS: VideoItem[] = [
  {
    id: 'showcase-video-1',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-facemask.mp4',
    posterUrl: '',
    alt: 'AI E-Commerce Video Example 1',
  },
  {
    id: 'showcase-video-2',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-earbuds.mp4',
    posterUrl: '',
    alt: 'AI E-Commerce Video Example 2',
  },
  {
    id: 'showcase-video-3',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-outfit.mp4',
    posterUrl: '',
    alt: 'AI E-Commerce Video Example 3',
  },
  {
    id: 'showcase-video-4',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-cream.mp4',
    posterUrl: '',
    alt: 'AI E-Commerce Video Example 4',
  },
  {
    id: 'showcase-video-5',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-perfume.mp4',
    posterUrl: '',
    alt: 'AI E-Commerce Video Example 5',
  },
  {
    id: 'showcase-video-6',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-energy-drink.mp4',
    posterUrl: '',
    alt: 'AI E-Commerce Video Example 6',
  },
  {
    id: 'showcase-video-7',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-product-review-reaction.mp4',
    posterUrl: '',
    alt: 'AI E-Commerce Video Example 7',
  },
];

/** Powerful AI Tools Section 配置 */
export const POWERFUL_TOOLS_CONFIG = {
  title: 'Powerful AI Tools',
};

/** Comparison Table Section 配置 */
export const COMPARISON_TABLE_CONFIG = {
  title: 'Why FalcoCut AI Video Agent is Different',
  subtitle:
    'Make studio-quality videos with AI voice cloning, avatars, face swap, and translation. Choose from 180+ voices, 50+ avatars, and 7+ languages to cut production time and costs instantly.',
};

/** Comparison Table 列配置 */
export const COMPARISON_COLUMNS: ComparisonColumn[] = [
  {
    id: 'traditional',
    label: 'Traditional',
    title: 'Manual Production',
    highlighted: false,
  },
  {
    id: 'current-gen',
    label: 'Current Gen',
    title: 'Video Generators',
    highlighted: false,
  },
  {
    id: 'next-gen',
    label: 'Next Gen',
    title: 'Video Agent',
    highlighted: true,
  },
];

/** Comparison Table 行数据 */
export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: 'Workflow',
    values: ['Studio-Based', 'Technical Input', 'Smart Guidance'],
  },
  {
    feature: 'Time to Create',
    values: ['Weeks / Months', 'Hours', 'Minutes'],
  },
  {
    feature: 'Cost',
    values: ['$600 – $5,000+', '$100 – $500+', 'From $1.5'],
  },
  {
    feature: 'Logic',
    values: ['Human Design', 'Frame Rendering', 'Ad Strategy'],
  },
  {
    feature: 'Iteration Speed',
    values: ['Days / Weeks', 'Hours of Fine-tuning', 'Seconds via Text'],
  },
  {
    feature: 'Scalability',
    values: ['Low', 'Medium', 'Unlimited'],
  },
];

/** ROI Stats Section 配置 */
export const ROI_STATS_CONFIG = {
  title: 'Multiply Your ROI by 2X, 5X, or More with FalcoCut AI-Powered Marketing Videos',
  ctaText: 'Create Videos Now',
  ctaUrl: '/ai-video-generator',
};

/** ROI Stats 卡片列表 */
export const ROI_STATS_CARDS: ROIStatCard[] = [
  {
    id: 'save-cost',
    title: 'Save Video Production Cost of',
    value: '$12,000 / month',
    description:
      'Replace expensive film crews and studio rentals with an automated AI video agent that works for a fraction of the cost.',
  },
  {
    id: 'conversion-rate',
    title: 'Increase Conversion Rate to',
    value: '72%',
    description:
      'Ditch static images for top-converting video demos and testimonials that earn buyer confidence and drive more checkouts.',
  },
  {
    id: 'engagement-rate',
    title: 'Boost Engagement Rate by',
    value: '+58%',
    description:
      'Stop the scroll and keep audiences watching longer with dynamic, AI-localized content that brings more clicks and likes.',
  },
  {
    id: 'scale-faster',
    title: 'Scale Faster by',
    value: '10X',
    description:
      'Launch new campaigns in minutes instead of weeks, allowing you to test more ideas and find winning ads faster.',
  },
];

/** FAQ Section 配置 */
export const FAQ_CONFIG = {
  title: 'Frequently Asked Questions',
};

/** FAQ 列表 */
export const FAQ_LIST: FAQItem[] = [
  {
    question: 'What is an AI Video Agent?',
    answer:
      'An AI Video Agent is a smart, creative partner that helps you turn ideas into polished videos with far less effort. It understands your goals, guides key steps, and handles time-consuming tasks like scene planning, pacing, and visual decisions. Instead of managing every detail yourself, you describe the direction and the agent brings it to life with speed and consistency. With FalcoCut, the agent becomes a reliable partner that produces high-quality videos faster while staying fully aligned with your creative vision.',
  },
  {
    question: 'What is FalcoCut and how does it work?',
    answer:
      "FalcoCut is an AI Video Agent that helps you turn simple ideas into complete videos without needing editing skills. You start by describing what you want to show or say, and the system organizes your thoughts into a clear structure. It then builds scenes, timing, and visuals automatically while letting you adjust anything you want along the way. This guided process makes it easy to create finished videos quickly, even if you're new to video production.",
  },
  {
    question: 'What types of videos can I create with FalcoCut?',
    answer:
      'FalcoCut lets you create a wide range of marketing videos, including product showcases, UGC-style clips, and [avatar-driven promotional content](/ai-avatar-generator). You can also produce e-commerce formats like [product try-ons](/ai-product-avatar), comparison videos, and quick highlight reels for social platforms. The system supports additional needs such as training content, educational explainers, and creator-focused storytelling with [video translation](/video-translator) for global reach. As FalcoCut evolves, it continues expanding into more scenarios for brands, educators, and everyday content creators.',
  },
  {
    question: 'How is FalcoCut different from traditional video production?',
    answer:
      'Traditional video tools make you manage every edit, transition, and effect yourself, which can feel slow and overwhelming for beginners. FalcoCut simplifies the process by understanding your instructions and automatically shaping scenes, pacing, and visuals around your goals. You just explain what you want, and our AI Video Agent produces a polished result far faster than manual editing.',
  },
  {
    question: 'Do I need video editing experience to use FalcoCut?',
    answer:
      "You don't need any editing background to use FalcoCut, because it guides you through each step clearly. Just explain the kind of video you want, and the system organizes your ideas into a clear, ready-to-use format. This makes creating professional-looking content simple for anyone, even if it's your first time making a video.",
  },
  {
    question: 'How long does it take to generate an AI video?',
    answer:
      'Most videos are created within minutes, giving you results quickly enough to review and adjust right away. The system processes your commands efficiently, turning your ideas into a complete draft without long waiting times. This fast turnaround helps you experiment freely and refine your content until it matches your goals.',
  },
  {
    question: 'Can I customize the AI-generated videos?',
    answer:
      "Yes, FalcoCut gives you extensive control, allowing you to guide the look, tone, and direction of each video with ease. You can share reference visuals, define preferences, and shape the output through clear instructions that reflect your brand's identity. The system adapts to your choices over time, helping you produce content that feels refined, consistent, and unmistakably yours.",
  },
  {
    question: 'Is FalcoCut free?',
    answer:
      "FalcoCut offers a free plan that includes 10 monthly credits, giving you access to essential creation tools from the start. This entry plan allows you to explore the platform, though exported videos include a watermark until you upgrade. When you're ready for expanded capabilities and higher credit limits, you can move to one of the paid plans designed for more advanced use.",
  },
  {
    question: "What can I do with FalcoCut's AI video tools?",
    listIntro:
      'FalcoCut offers a refined suite of capabilities that help you craft sophisticated content with clarity, speed, and creative control.',
    unorderedList: [
      {
        title: 'Add AI Avatars:',
        content:
          'Elevate your message with [AI presenters](/ai-avatar-generator) that deliver consistent on-screen communication without arranging any filming logistics.',
      },
      {
        title: 'Edit Videos:',
        content:
          'Reshape your footage with guided adjustments in our [AI video editor](/ai-video-editor) that help refine timing, structure, and flow without navigating complex timelines.',
      },
      {
        title: 'Clone Voices:',
        content:
          'Generate expressive narration with [voice cloning](/ai-voice-cloning), selecting from a wide range of voices tailored to different audiences and tones.',
      },
      {
        title: 'Text to Video:',
        content:
          'Convert rough scripts into complete video formats with our [AI video generator](/ai-video-generator), streamlining production for fast-moving teams and creators.',
      },
      {
        title: 'Localize Videos:',
        content:
          'Adapt your content for global reach through [instant language transformation](/video-translator) that preserves nuance and brand character.',
      },
      {
        title: 'Animate Photos:',
        content:
          'Turn static images into dynamic motion pieces with our [AI image generator](/ai-image-generator) that enhance product storytelling, brand expression, and educational communication.',
      },
    ],
  },
  {
    question: 'How to make AI videos using FalcoCut?',
    orderedList: [
      {
        title: 'Step 1:',
        content:
          'Go to the [AI Video Generator](/ai-video-generator) and start by writing a short prompt or uploading an image to show what kind of video you want.',
      },
      {
        title: 'Step 2:',
        content:
          'Choose an AI model that matches your style, which depends on your project.',
      },
      {
        title: 'Step 3:',
        content:
          'Set the video length and quality, like how many seconds and what resolution you need.',
      },
      {
        title: 'Step 4:',
        content: 'Click to generate your video, then download it or share it online.',
      },
      {
        content:
          'If you want to improve it later, you can go back and adjust your prompt or settings anytime.',
      },
    ],
  },
  {
    question: 'Why is there a watermark on my video?',
    answer:
      'Watermarks appear on videos exported using the free plan, which includes limited monthly credits for basic access. To remove the watermark and unlock higher-tier features, you can upgrade to a paid plan with expanded usage options. It ensures fair usage while allowing creators to explore the platform before committing to advanced production capabilities.',
  },
  {
    question: 'Can I access FalcoCut on mobile devices?',
    answer:
      "Yes. FalcoCut works smoothly on mobile browsers, allowing you to create and manage projects directly from your phone. You can review drafts, adjust settings, and export content on the go, giving you flexibility wherever you're working.",
  },
  {
    question: 'Is my data safe and secure at FalcoCut?',
    answer:
      'Yes. FalcoCut safeguards your information with advanced protection standards designed to keep your files, prompts, and project details confidential throughout your workflow. These measures ensure your materials remain exclusively within your account and are never accessed, shared, or repurposed without your authorization. We take this responsibility seriously, giving you confidence that your creative assets are handled with discretion, integrity, and enterprise-grade protection at every stage.',
  },
];

/** Footer CTA Section 配置 */
export const FOOTER_CTA_CONFIG = {
  title: 'Scale Your Video Marketing with AI',
  subtitle:
    'FalcoCut helps you turn product assets into powerful video ads—10× faster and at a fraction of the cost. From product demos to multilingual campaigns, you can engage global customers and drive sales effortlessly.',
  ctaText: 'Create Your Video Now',
  ctaUrl: '/ai-video-generator',
};
